import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EditorModule } from '@tinymce/tinymce-angular';
import { ProductService, Product, PriceOption, WcCategory } from '../../services/product.service';
import { NotificationService } from '../../services/notification.service';
import { NotificationComponent } from '../../components/notification/notification.component';
import { WebsiteService } from '../../services/website.service';
import { ImageUploadService } from '../../services/image-upload.service';
import { MediaSelectorModalComponent } from '../pages/page-editor/components/media-selector-modal/media-selector-modal.component';
import { environment } from '../../../environments/environment';

type SupportedLanguage = 'de' | 'en' | 'es' | 'fr' | 'it' | 'nl';

@Component({
  selector: 'app-edit-product',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    EditorModule,
    NotificationComponent,
    MediaSelectorModalComponent
  ],
  templateUrl: './edit-product.component.html',
  styleUrls: ['./edit-product.component.scss']
})
export class EditProductComponent implements OnInit, OnDestroy {
  productForm: FormGroup;
  product: Product | null = null;
  productId: string | null = null;

  /**
   * Vercel preview URL for this product, scoped to the current domain.
   * ?domain= triggers cache bypass on the frontend.
   */
  get previewUrl(): string | null {
    if (!this.productId) return null;
    const domain = this.websiteService.getCurrentDomain();
    if (!domain) return null;
    return `${environment.previewBaseUrl}/product/${encodeURIComponent(this.productId)}?domain=${encodeURIComponent(domain)}`;
  }

  isLoading = false;
  isSaving = false;
  priceOptions: PriceOption[] = [];
  activeTab: string = 'basic';
  languages: SupportedLanguage[] = ['nl', 'en', 'de', 'es', 'fr', 'it'];
  selectedLanguages: SupportedLanguage[] = ['nl', 'en']; // Default to NL and EN
  languageNames: { [key: string]: string } = {
    'nl': 'Nederlands',
    'en': 'English',
    'de': 'Deutsch',
    'es': 'Español',
    'fr': 'Français',
    'it': 'Italiano'
  };
  showLanguageSelector: boolean = false;

  // Product images (gallery)
  productImages: { src: string }[] = [];
  primaryImageIndex = 0;
  showMediaSelector: boolean = false;
  imageUploading: boolean = false;
  imageUploadProgress: number = 0;

  // Category autocomplete
  categories: WcCategory[] = [];
  categoryDropdownOpen: { [key: string]: boolean } = {};
  categorySearchText: { [key: string]: string } = {};

  // ACF fields for different languages
  acfFields: { [key: string]: any } = {};

  editorConfig = {
    base_url: '/tinymce',
    suffix: '.min',
    promotion: false,
    skin: 'oxide-dark',
    content_css: 'dark',
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'help', 'wordcount',
      'imagetools', 'quickbars', 'paste'
    ],
    toolbar: 'undo redo | blocks | ' +
      'bold italic forecolor | alignleft aligncenter ' +
      'alignright alignjustify | bullist numlist outdent indent | ' +
      'image media table | styleselect | help',
    image_advtab: true,
    height: 400,
    content_style: 'body { background-color: #1F2937; color: #D1D5DB; }'
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductService,
    private notificationService: NotificationService,
    private websiteService: WebsiteService,
    private imageUploadService: ImageUploadService
  ) {
    this.productForm = this.fb.group({
      sku: [''],
      price: [0, [Validators.required, Validators.min(0)]],
      on_sale: [false],
      sale_price: [0, Validators.min(0)],
      saleFrom: [''],
      saleTo: [''],
      stock_quantity: [0, [Validators.required, Validators.min(0)]],
      stock_status: ['instock', Validators.required],
      status: ['publish', Validators.required],
      weight: [0, Validators.min(0)]
    });
  }

  async ngOnInit() {
    this.productId = this.route.snapshot.paramMap.get('id');
    if (this.productId) {
      await Promise.all([
        this.loadWebsiteSettings(),
        this.loadCategories(),
        this.loadProduct()
      ]);
    } else {
      this.notificationService.show({
        type: 'error',
        message: 'Product ID is missing'
      });
      this.router.navigate(['/webshop']);
    }
  }

  ngOnDestroy() {
    // Cleanup if needed
  }

  private async loadWebsiteSettings(): Promise<void> {
    const currentDomain = this.websiteService.getCurrentDomain();
    if (!currentDomain) return;

    const settings = await this.websiteService.getWebsiteSettings(currentDomain);
    if (settings && settings.availableLanguages && settings.availableLanguages.length > 0) {
      // Update languages array from website settings
      this.languages = settings.availableLanguages.map(lang => lang.code as SupportedLanguage);
      
      // Update languageNames object from website settings
      this.languageNames = {};
      settings.availableLanguages.forEach(lang => {
        this.languageNames[lang.code] = lang.name;
      });
      
      // Try to restore selected languages from localStorage (user-specific, not product-specific)
      const storedLanguages = this.getStoredSelectedLanguages();
      if (storedLanguages && storedLanguages.length > 0) {
        // Only use stored languages that are still available
        const filteredLanguages = storedLanguages.filter(lang => 
          this.languages.includes(lang)
        ) as SupportedLanguage[];
        
        if (filteredLanguages.length > 0) {
          // Use stored languages if they match available languages
          // Sort them to maintain order based on available languages
          this.selectedLanguages = filteredLanguages.sort((a, b) => {
            return this.languages.indexOf(a) - this.languages.indexOf(b);
          });
          console.log('Restored and set selectedLanguages to:', this.selectedLanguages);
        } else {
          // Stored languages don't match available languages, use default
          const defaultLang = settings.defaultLanguage || settings.availableLanguages[0]?.code;
          if (defaultLang && this.languages.includes(defaultLang as SupportedLanguage)) {
            this.selectedLanguages = [defaultLang as SupportedLanguage];
          } else if (this.languages.length > 0) {
            this.selectedLanguages = [this.languages[0]];
          }
        }
      } else {
        // No stored languages, use default
        const defaultLang = settings.defaultLanguage || settings.availableLanguages[0]?.code;
        if (defaultLang && this.languages.includes(defaultLang as SupportedLanguage)) {
          this.selectedLanguages = [defaultLang as SupportedLanguage];
        } else if (this.languages.length > 0) {
          this.selectedLanguages = [this.languages[0]];
        }
      }
    }
  }

  private getStoredSelectedLanguages(): SupportedLanguage[] | null {
    try {
      const stored = localStorage.getItem('user_selected_languages');
      if (stored) {
        const parsed = JSON.parse(stored) as SupportedLanguage[];
        console.log('Restored selected languages from localStorage:', parsed);
        return parsed;
      }
    } catch (error) {
      console.error('Error reading stored languages:', error);
    }
    return null;
  }

  private saveSelectedLanguages(): void {
    try {
      localStorage.setItem('user_selected_languages', JSON.stringify(this.selectedLanguages));
      console.log('Saved selected languages to localStorage:', this.selectedLanguages);
    } catch (error) {
      console.error('Error saving selected languages:', error);
    }
  }

  private detectLanguagesFromProduct(): void {
    if (!this.product) return;

    // If user has stored preferences, don't auto-detect languages - use stored preferences only
    const hasStoredLanguages = this.getStoredSelectedLanguages() !== null;
    if (hasStoredLanguages) {
      console.log('User has stored language preferences, skipping auto-detection. Using stored selection:', this.selectedLanguages);
      return;
    }
    
    // Only auto-detect if no stored preferences exist
    const beforeDetection = [...this.selectedLanguages];
    const detectedLanguages: SupportedLanguage[] = [];
    
    // Check ACF fields for language-specific content
    if (this.product.acf) {
      const acfKeys = Object.keys(this.product.acf);
      
      // Common ACF field patterns: naam_nl, omschrijving_en, etc.
      const languagePatterns = ['naam', 'omschrijving', 'handleiding', 'veelgestelde_vragen', 'slug'];
      
      this.languages.forEach(lang => {
        // Check if any ACF field exists for this language
        const hasContent = languagePatterns.some(pattern => {
          const fieldKey = `${pattern}_${lang}`;
          const value = this.acfFields[fieldKey];
          return value && (typeof value === 'string' ? value.trim() !== '' : value !== null && value !== undefined);
        });
        
        // Also check root-level fields like meta_title_en, main_cat_de, etc.
        const rootFields = ['meta_title', 'meta_desc', 'main_cat', 'url'];
        const hasRootContent = rootFields.some(pattern => {
          const fieldKey = `${pattern}_${lang}`;
          const value = (this.product as any)?.[fieldKey];
          return value && (typeof value === 'string' ? value.trim() !== '' : value !== null && value !== undefined);
        });
        
        if (hasContent || hasRootContent) {
          detectedLanguages.push(lang);
        }
      });
    }
    
    // Merge detected languages with current selection (only when no stored preferences)
    if (detectedLanguages.length > 0) {
      const merged = [...new Set([...this.selectedLanguages, ...detectedLanguages])];
      this.selectedLanguages = merged
        .filter(lang => this.languages.includes(lang))
        .sort((a, b) => this.languages.indexOf(a) - this.languages.indexOf(b)) as SupportedLanguage[];
      // Languages detected and merged
    } else {
      // No languages detected, keeping current selection
    }
  }

  async loadProduct() {
    if (!this.productId) return;

    this.isLoading = true;
    try {
      this.product = await this.productService.getProductById(this.productId);
      if (this.product) {
        // Load basic form fields
        const salePrice = (this.product as any).sale_price || 0;
        const onSale = (this.product as any).on_sale || (salePrice > 0);
        
        // Convert ISO date strings to YYYY-MM-DD format for date inputs
        const saleFrom = (this.product as any).saleFrom 
          ? new Date((this.product as any).saleFrom).toISOString().split('T')[0]
          : '';
        const saleTo = (this.product as any).saleTo
          ? new Date((this.product as any).saleTo).toISOString().split('T')[0]
          : '';
        
        this.productForm.patchValue({
          sku: this.product.sku || '',
          price: this.product.price || 0,
          on_sale: onSale,
          sale_price: salePrice,
          saleFrom: saleFrom,
          saleTo: saleTo,
          stock_quantity: this.product.stock_quantity || 0,
          stock_status: this.product.stock_status || 'instock',
          status: (this.product as any).status || 'publish',
          weight: (this.product as any).weight || 0
        });

        // Load ACF fields
        if (this.product.acf) {
          this.acfFields = JSON.parse(JSON.stringify(this.product.acf));
        }

        // Load product images gallery
        const existingImages = (this.product as any).images || [];
        const singleImage = (this.product as any).image;
        if (existingImages.length > 0) {
          this.productImages = existingImages.filter((img: any) => img?.src);
        } else if (singleImage) {
          this.productImages = [{ src: singleImage }];
        } else {
          this.productImages = [];
        }
        this.primaryImageIndex = 0;

        // Load price options if they exist, ensure defaults
        if (this.product.price_options && this.product.price_options.length > 0) {
          this.priceOptions = JSON.parse(JSON.stringify(this.product.price_options)).map((opt: any) => ({
            ...opt,
            stock_status: opt.stock_status || 'instock',
            stock_quantity: opt.stock_quantity ?? 0,
            enabled: opt.enabled !== false
          }));
        }

        // Detect languages from product content and update selected languages
        this.detectLanguagesFromProduct();
        
        // Languages initialized
      } else {
        this.notificationService.show({
          type: 'error',
          message: 'Product not found'
        });
        this.router.navigate(['/webshop']);
      }
    } catch (error) {
      console.error('Error loading product:', error);
      this.notificationService.show({
        type: 'error',
        message: 'Failed to load product'
      });
      this.router.navigate(['/webshop']);
    } finally {
      this.isLoading = false;
    }
  }

  // Helper to get ACF field value
  getAcfField(field: string, lang: string = 'nl'): string {
    const key = `${field}_${lang}`;
    return this.acfFields[key] || '';
  }

  // Helper to set ACF field value
  setAcfField(field: string, lang: string, value: string): void {
    const key = `${field}_${lang}`;
    this.acfFields[key] = value;
  }

  // Helper to get root-level SEO field value (meta_title_*, meta_desc_*, etc.)
  getSeoField(field: string, lang: string = 'nl'): string {
    if (!this.product) return '';
    const key = `${field}_${lang}`;
    return (this.product as any)[key] || '';
  }

  // Helper to set root-level SEO field value (meta_title_*, meta_desc_*, etc.)
  setSeoField(field: string, lang: string, value: string): void {
    if (!this.product) return;
    const key = `${field}_${lang}`;
    (this.product as any)[key] = value;
  }

  // Helper to get root-level category field value (main_cat_*, url_*, etc.)
  getCategoryField(field: string, lang: string = 'nl'): string {
    if (!this.product) return '';
    const key = `${field}_${lang}`;
    return (this.product as any)[key] || '';
  }

  // Helper to set root-level category field value (main_cat_*, url_*, etc.)
  setCategoryField(field: string, lang: string, value: string): void {
    if (!this.product) return;
    const key = `${field}_${lang}`;
    (this.product as any)[key] = value;
  }

  async saveProduct() {
    if (this.productForm.invalid || !this.productId) {
      this.notificationService.show({
        type: 'error',
        message: 'Please fill in all required fields correctly'
      });
      return;
    }

    this.isSaving = true;
    try {
      const formValue = this.productForm.value;
      
      // Prepare ACF data - preserve all existing fields and update with new values
      const acfData: { [key: string]: any } = this.product?.acf 
        ? { ...this.product.acf }
        : {};
      
      // Update all ACF fields from our local object
      Object.keys(this.acfFields).forEach(key => {
        acfData[key] = this.acfFields[key];
      });
      
      // Convert date inputs to ISO format
      const saleFromISO = formValue.saleFrom 
        ? new Date(formValue.saleFrom + 'T00:00:00Z').toISOString()
        : null;
      const saleToISO = formValue.saleTo
        ? new Date(formValue.saleTo + 'T23:59:59Z').toISOString()
        : null;

      // Prepare product data for update
      const stockQty = parseInt(formValue.stock_quantity, 10) || 0;
      const productData: any = {
        sku: formValue.sku,
        acf: acfData,
        price: parseFloat(formValue.price),
        on_sale: formValue.on_sale || false,
        stock_quantity: stockQty,
        stock: stockQty, // shop reads 'stock' field for canAddToCart
        stock_status: formValue.stock_status,
        status: formValue.status
      };

      // Ensure name fields exist for the shop (reads acf.naam_* and name)
      const primaryName = acfData['naam_nl'] || acfData['naam_en'] || formValue.sku || '';
      if (primaryName) {
        productData.name = primaryName;
        productData.name_nl = acfData['naam_nl'] || primaryName;
      }

      // Only add sale_price if on_sale is true and sale_price has a value
      if (formValue.on_sale && formValue.sale_price) {
        productData.sale_price = parseFloat(formValue.sale_price);
      }

      // Only add sale dates if on_sale is true
      if (formValue.on_sale) {
        if (saleFromISO) {
          productData.saleFrom = saleFromISO;
        }
        if (saleToISO) {
          productData.saleTo = saleToISO;
        }
      }

      // Only add weight if it has a value
      if (formValue.weight) {
        productData.weight = parseFloat(formValue.weight);
      }

      // Save product images — reorder so primary is first for shop compatibility
      const orderedImages = [...this.productImages];
      if (this.primaryImageIndex > 0 && this.primaryImageIndex < orderedImages.length) {
        const primary = orderedImages.splice(this.primaryImageIndex, 1)[0];
        orderedImages.unshift(primary);
      }
      productData.images = orderedImages;
      productData.image = orderedImages[0]?.src || null;

      // Preserve and update root-level SEO fields (meta_title_*, meta_desc_*)
      const languages = ['nl', 'en', 'de', 'es', 'fr', 'it'];
      languages.forEach(lang => {
        const metaTitleKey = `meta_title_${lang}`;
        const metaDescKey = `meta_desc_${lang}`;
        if ((this.product as any)?.[metaTitleKey] !== undefined) {
          productData[metaTitleKey] = (this.product as any)[metaTitleKey];
        }
        if ((this.product as any)?.[metaDescKey] !== undefined) {
          productData[metaDescKey] = (this.product as any)[metaDescKey];
        }
      });

      // Preserve and update root-level category fields (main_cat_*, url_*)
      languages.forEach(lang => {
        const mainCatKey = `main_cat_${lang}`;
        const urlKey = `url_${lang}`;
        if ((this.product as any)?.[mainCatKey] !== undefined) {
          productData[mainCatKey] = (this.product as any)[mainCatKey];
        }
        if ((this.product as any)?.[urlKey] !== undefined) {
          productData[urlKey] = (this.product as any)[urlKey];
        }
      });

      // Include price options (preserve existing if empty, or use new ones)
      if (this.priceOptions.length > 0) {
        productData.price_options = this.priceOptions;
      } else if (this.product?.price_options && this.product.price_options.length > 0) {
        // If form has no price options but product had them, remove them
        productData.price_options = [];
      }

      await this.productService.updateProduct(this.productId, productData);
      
      this.notificationService.show({
        type: 'success',
        message: 'Product opgeslagen'
      });
    } catch (error) {
      console.error('Error saving product:', error);
      this.notificationService.show({
        type: 'error',
        message: 'Failed to save product'
      });
    } finally {
      this.isSaving = false;
    }
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
  }

  toggleLanguage(lang: SupportedLanguage) {
    const index = this.selectedLanguages.indexOf(lang);
    if (index > -1) {
      this.selectedLanguages.splice(index, 1);
    } else {
      this.selectedLanguages.push(lang);
    }
    // Sort selected languages to maintain order
    this.selectedLanguages.sort((a, b) => {
      return this.languages.indexOf(a) - this.languages.indexOf(b);
    });
    // Save selected languages to localStorage
    this.saveSelectedLanguages();
  }

  isLanguageSelected(lang: SupportedLanguage): boolean {
    const isSelected = this.selectedLanguages.includes(lang);
    // Debug logging (can be removed later)
    if (this.selectedLanguages.length > 0) {
      console.log(`Checking if ${lang} is selected. Current selectedLanguages:`, this.selectedLanguages, 'Result:', isSelected);
    }
    return isSelected;
  }

  getSelectedLanguages(): SupportedLanguage[] {
    return this.selectedLanguages;
  }

  // ── Image Management ──────────────────────────────────────────────

  onImageSelected(url: string) {
    this.productImages.push({ src: url });
    this.showMediaSelector = false;
  }

  async uploadImage(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.imageUploading = true;
    this.imageUploadProgress = 0;
    try {
      const variants = await this.imageUploadService.uploadImageWithVariants(
        file,
        (progress) => { this.imageUploadProgress = progress.progress; }
      );
      this.productImages.push({ src: variants.webp1400 || variants.original });
      this.notificationService.show({ type: 'success', message: 'Afbeelding geüpload' });
    } catch (error) {
      console.error('Image upload failed:', error);
      this.notificationService.show({ type: 'error', message: 'Upload mislukt' });
    } finally {
      this.imageUploading = false;
      input.value = '';
    }
  }

  removeImage(index: number) {
    this.productImages.splice(index, 1);
    if (this.primaryImageIndex >= this.productImages.length) {
      this.primaryImageIndex = Math.max(0, this.productImages.length - 1);
    } else if (this.primaryImageIndex > index) {
      this.primaryImageIndex--;
    }
  }

  setPrimaryImage(index: number) {
    this.primaryImageIndex = index;
  }

  moveImageLeft(index: number) {
    if (index <= 0) return;
    const temp = this.productImages[index];
    this.productImages[index] = this.productImages[index - 1];
    this.productImages[index - 1] = temp;
    if (this.primaryImageIndex === index) this.primaryImageIndex--;
    else if (this.primaryImageIndex === index - 1) this.primaryImageIndex++;
  }

  moveImageRight(index: number) {
    if (index >= this.productImages.length - 1) return;
    const temp = this.productImages[index];
    this.productImages[index] = this.productImages[index + 1];
    this.productImages[index + 1] = temp;
    if (this.primaryImageIndex === index) this.primaryImageIndex++;
    else if (this.primaryImageIndex === index + 1) this.primaryImageIndex--;
  }

  get productImage(): string | null {
    return this.productImages[this.primaryImageIndex]?.src || null;
  }

  // ── Category Autocomplete ──────────────────────────────────────────

  private async loadCategories() {
    try {
      this.categories = await this.productService.getWooCommerceCategories();
    } catch (e) {
      console.error('Error loading WC categories:', e);
    }
  }

  getFilteredCategories(lang: string): { category: WcCategory; depth: number }[] {
    const search = (this.categorySearchText[lang] || '').toLowerCase().trim();
    const filtered = search
      ? this.categories.filter(c => c.name.toLowerCase().includes(search))
      : this.categories;
    return this.buildCategoryTree(filtered);
  }

  private buildCategoryTree(cats: WcCategory[]): { category: WcCategory; depth: number }[] {
    const allCats = this.categories;
    const result: { category: WcCategory; depth: number }[] = [];
    const catSet = new Set(cats.map(c => c.id));

    const addChildren = (parentId: number, depth: number) => {
      const children = allCats
        .filter(c => c.parent === parentId && catSet.has(c.id))
        .sort((a, b) => a.name.localeCompare(b.name));
      for (const child of children) {
        result.push({ category: child, depth });
        addChildren(child.id, depth + 1);
      }
    };

    // Start with root categories (parent === 0)
    addChildren(0, 0);

    // Add any orphans that didn't match the tree (e.g. when filtering)
    for (const cat of cats) {
      if (!result.find(r => r.category.id === cat.id)) {
        result.push({ category: cat, depth: 0 });
      }
    }

    return result;
  }

  onCategoryInput(lang: string, value: string) {
    this.setCategoryField('main_cat', lang, value);
    this.categorySearchText[lang] = value;
    this.categoryDropdownOpen[lang] = true;
  }

  onCategoryFocus(lang: string) {
    this.categorySearchText[lang] = this.getCategoryField('main_cat', lang);
    this.categoryDropdownOpen[lang] = true;
  }

  onCategoryBlur(lang: string) {
    // Delay to allow click on dropdown item
    setTimeout(() => { this.categoryDropdownOpen[lang] = false; }, 200);
  }

  selectCategory(lang: string, cat: WcCategory) {
    this.setCategoryField('main_cat', lang, cat.name);
    this.categorySearchText[lang] = cat.name;
    this.categoryDropdownOpen[lang] = false;
  }

  async saveAndClose() {
    await this.saveProduct();
    if (!this.isSaving) {
      this.router.navigate(['/webshop']);
    }
  }

  cancel() {
    this.router.navigate(['/webshop']);
  }

  // Price Options Management
  addPriceOption() {
    const newOption: PriceOption = {
      name: '',
      name_de: '',
      name_en: '',
      name_es: '',
      name_fr: '',
      name_nl: '',
      order: this.priceOptions.length,
      price: 0,
      var_id: Date.now(),
      stock_quantity: 0,
      stock_status: 'instock',
      enabled: true
    };
    this.priceOptions.push(newOption);
  }

  togglePriceOption(index: number) {
    this.priceOptions[index].enabled = !this.priceOptions[index].enabled;
  }

  removePriceOption(index: number) {
    this.priceOptions.splice(index, 1);
    // Update order values
    this.priceOptions.forEach((option, idx) => {
      option.order = idx;
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
}

